import { isKnownState, type Version } from './versions-api';

/**
 * PH-28 — WHICH LIFECYCLE ACTIONS A VERSION OFFERS, and what each one is going to do.
 *
 * ⭐⭐ WHY THIS IS A SEPARATE, REACT-FREE FILE. Every line is a claim about the API's refusals, and
 * those are exactly the claims that rot silently inside a component. Getting one wrong produces a
 * button whose only possible outcome is a 409 — which is what "Start sprint on an active sprint"
 * would have been, and what PH-23's tests were written to stop.
 *
 * 🔴 THE REFUSALS, READ OFF `services/versions.ts` RATHER THAN GUESSED:
 *   · release  on `released` → 409 `version 'X' is already released`
 *   · release  on `archived` → 409 `version 'X' is archived; unarchive it before releasing`
 *   · archive  on `archived` → 409 `version 'X' is already archived`
 *   · unarchive on anything not archived → 409 `version 'X' is not archived`
 * ⇒ Each action is offered on exactly the states where it will not immediately 409.
 *
 * ⛔ THIS IS NOT THE VALIDATION. The API re-checks all of it, and its refusal is rendered verbatim
 * when someone else changes the state between the render and the click. Hiding a button is about not
 * inviting a click that cannot work; it is never the rule.
 */

export type VersionAction = 'release' | 'archive' | 'unarchive';

/**
 * ⭐ ORDER IS THE OFFERED ORDER, and the irreversible one is FIRST rather than last. On a 390px card
 * the buttons wrap; leaving Release to the end would put the consequential action in whatever
 * position the wrap happened to leave it.
 */
export function actionsFor(state: string): VersionAction[] {
  /**
   * ⚠️ AN UNKNOWN STATE OFFERS NOTHING. db/001's CHECK allows three values today and a migration may
   * add a fourth; this build cannot know what that fourth one permits. ⛔ Falling through to the
   * `unreleased` branch would offer Release on a state that might already be terminal — the same
   * "bucket the unknown into the familiar" error `isKnownState` exists to prevent when RENDERING it.
   */
  if (!isKnownState(state)) return [];
  if (state === 'archived') return ['unarchive'];
  if (state === 'released') return ['archive'];
  return ['release', 'archive'];
}

export function actionLabel(action: VersionAction): string {
  return action === 'release' ? 'Release' : action === 'archive' ? 'Archive' : 'Unarchive';
}

/**
 * 🔴🔴 WHAT IS ABOUT TO HAPPEN, STATED BEFORE THE BUTTON THAT DOES IT — the `CompleteSprintDialog`
 * rule, applied to the one version action that is effectively a one-way door.
 *
 * ⚠️ "IRREVERSIBLE" IS A MEASURED CLAIM, NOT A FEELING. There is no un-release endpoint at all:
 * `routes/versions.ts` serves release, archive and unarchive and nothing else, and releasing twice
 * answers 409 `is already released`. The nearest thing to an undo is archive-then-unarchive, and
 * that does NOT undo it — `unarchiveVersion` derives its destination from `release_date`, so a
 * released version comes back RELEASED. ⇒ The only way back is an UPDATE by hand in production.
 */
export const RELEASE_CONSEQUENCE =
  'Releasing is effectively permanent. There is no un-release endpoint, and a second release answers ' +
  '409 "already released". Archiving it afterwards does not undo it either: unarchiving derives the ' +
  'state from the release date, so it returns to RELEASED, not to unreleased. The only way back is an ' +
  'UPDATE by hand in the database.';

/**
 * ⭐ ARCHIVE IS THE OPPOSITE CASE AND IS ALLOWED TO BE ONE CLICK. The API grew `unarchive`
 * specifically *"so archive is not a one-way door"* — one mis-click costs one more click. ⛔ Wrapping
 * a reversible action in the same confirmation as an irreversible one teaches people to click
 * through confirmations, which is how the irreversible one gets clicked through too.
 */
export function needsConfirmation(action: VersionAction): boolean {
  return action === 'release';
}

/**
 * What ARCHIVING is and is not — shown on the card, not in a dialog.
 *
 * ⚠️ The half people get wrong is that archiving does NOT detach tickets: `ticket.version_id` and
 * `ticket_fix_version` rows survive, because a ticket that shipped in v1.0.33 shipped in v1.0.33
 * whether or not anybody still wants that release in a picker.
 */
export const ARCHIVE_NOTE =
  'Archiving hides a release from the pickers. It keeps every ticket link, and Unarchive puts it back.';

/**
 * ⭐ WHAT THE SERVER WILL SET, said BEFORE the click, so the surprise is not the outcome.
 * ⛔ It is a PREDICTION for the label only — the row that gets rendered afterwards is the server's,
 * never this. The two agreeing is a nice-to-have; the row being the server's is the requirement.
 */
export function unarchiveDestination(version: Pick<Version, 'release_date'>): string {
  return version.release_date ? 'released' : 'unreleased';
}
